'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Section } from './section';
import { Button } from './button';

interface Tool {
  name: string;
  slug: string;
  description: string;
}

interface BlogPost {
  slug: string;
  title: string;
}

export default function Footer() {
  // Define latest tools
  const latestTools: Tool[] = [
    {
      name: 'LinkedIn Banner Generator',
      slug: 'linkedin-banner-generator',
      description: 'Create professional LinkedIn banners'
    },
    {
      name: 'Job Seeker Mentor',
      slug: 'job-seeker-mentor',
      description: 'Get personalized job search guidance'
    },
    {
      name: 'LinkedIn Feedback',
      slug: 'linkedin-feedback',
      description: 'Optimize your LinkedIn profile'
    },
    {
      name: 'Resume Feedback',
      slug: 'resume-feedback',
      description: 'Stand out with expert resume feedback'
    },
    {
      name: 'Portfolio Builder',
      slug: 'portfolio-builder',
      description: 'Showcase your work effectively'
    }
  ];

  // State for blog posts
  const [latestBlogs, setLatestBlogs] = useState<BlogPost[]>([]);

  // Fetch latest blog posts from API
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch('/api/blogs');
        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }
        const data = await response.json();
        setLatestBlogs(data);
      } catch (error) {
        console.error('Failed to fetch blog posts:', error);
        // Fallback to empty array if fetch fails
        setLatestBlogs([]);
      }
    };

    fetchBlogs();
  }, []);

  return (
    <div>
      <Section className="relative pb-40 px-4">
        <div className="max-w-3xl mx-auto md:text-center">
          <h2 className="text-6xl font-bold font-domaine-narrow mb-6">
            Ready to Transform Your Tech Hiring?
          </h2>
          <p className="text-lg mb-8 text-gray-600">
            Schedule a demo to see our AI interviewer in action and learn how we
            can help you hire faster and better.
          </p>
          <Link href="/signup?role=company">
            <Button size="xl">Start hiring</Button>
          </Link>
        </div>
      </Section>

      <footer className="bg-white border-t border-gray-200 relative xl:h-screen">
        <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-t from-white via-white/90 to-transparent" />
        {/* <div className="hidden xl:block"> */}
        <Image
          src="/footer-bg-1.png"
          alt="Footer Background"
          layout="fill"
          objectFit="cover"
          priority={true} // Optional: prioritize loading of this image
          className=" absolute top-0 left-0 z-0 opacity-20"
        />
        {/* </div> */}
        <div className="py-20 relative z-10">
          <h2 className="text-center text-[64px] md:text-[128px] lg:text-[164px] xl:text-[248px] font-regular font-domaine-narrow leading-none text-black/30">
            Cruit Europe
          </h2>
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Logo Section */}
            <div className="lg:col-span-1">
              <Link href="/" className="flex items-center">
                <Image
                  src="/cruit-eu-stars.svg"
                  alt="Cruit Europe Logo"
                  width={40}
                  height={40}
                />
                <span className="ml-2 text-xl font-bold">Cruit Europe</span>
              </Link>
              <p className="mt-4 text-sm text-gray-600">
                Find, Contract & Manage Remote Developers across Europe
              </p>
            </div>

            {/* Company Section */}
            <div className="lg:col-span-1">
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                Company
              </h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    href="/companies"
                    className="text-base text-gray-600 hover:text-gray-900"
                  >
                    Start hiring
                  </Link>
                </li>
                <li>
                  <Link
                    href="/login"
                    className="text-base text-gray-600 hover:text-gray-900"
                  >
                    Login
                  </Link>
                </li>
              </ul>
            </div>

            {/* Candidate Section */}
            <div className="lg:col-span-1">
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                Candidate
              </h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    href="/signup?role=candidate"
                    className="text-base text-gray-600 hover:text-gray-900"
                  >
                    Create account
                  </Link>
                </li>
                <li>
                  <Link
                    href="/login"
                    className="text-base text-gray-600 hover:text-gray-900"
                  >
                    Login
                  </Link>
                </li>
              </ul>
            </div>

            {/* Tools Section */}
            <div className="lg:col-span-1">
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                Tools
              </h3>
              <ul className="mt-4 space-y-2">
                <li>
                  {/* <Link
                    href="/tools"
                    className="text-base text-gray-600 hover:text-gray-900"
                  > */}
                  {/* Overview */}
                  Coming soon
                  {/* </Link> */}
                </li>
                {/* {latestTools.slice(0, 5).map((tool) => (
                  <li key={tool.slug}>
                    <Link
                      href={`/tools/${tool.slug}`}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      {tool.name}
                    </Link>
                  </li>
                ))} */}
              </ul>
            </div>

            {/* Blogs Section */}
            <div className="lg:col-span-1">
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                Blogs
              </h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    href="/blogs"
                    className="text-base text-gray-600 hover:text-gray-900"
                  >
                    Overview
                  </Link>
                </li>
                {latestBlogs.map(blog => (
                  <li key={blog.slug}>
                    <Link
                      href={`/blogs/${blog.slug}`}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      {blog.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Terms Section */}
            <div className="lg:col-span-1">
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">
                Legal
              </h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    href="/terms"
                    className="text-base text-gray-600 hover:text-gray-900"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    href="/privacy"
                    className="text-base text-gray-600 hover:text-gray-900"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    href="/cookies"
                    className="text-base text-gray-600 hover:text-gray-900"
                  >
                    Cookie Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 border-t border-gray-200 pt-8">
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} Cruit Europe. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
